import { useEffect, useState } from 'react'
import type { Incident, RadioSeat } from '../lib/api'
import type { PanelTx, PanelGroup } from './PanelHost'
import './dispatch.css'

/** The radio strip: what the panel is doing, and the way onto the air.
 *
 *  IT MIRRORS THE PANEL. IT DOES NOT DUPLICATE IT.
 *
 *  Every value here comes from the panel over postMessage — the seat, the
 *  groups, who is transmitting. Nothing is invented locally, because a strip
 *  that shows a group the panel does not have, or silence while somebody is
 *  talking, is worse than one that admits it does not know.
 *
 *  Three of the things it should show need messages the panel does not send
 *  yet. Each is marked below and asked for in HANDOVER-CRM-CONNECTIONS.md. */
export function RadioBar({
  seat,
  seats,
  selected,
  note,
  talking,
  groups,
  group,
  onGroup,
  transmit,
  panelElsewhere,
}: {
  seat: RadioSeat | null
  seats: RadioSeat[]
  selected: Incident | null
  note: string | null
  /** Who is on the air right now. `mine` is this dispatcher. */
  talking: PanelTx | null
  /** The panel's own talk groups. Empty until it tells us what they are. */
  groups: PanelGroup[]
  group: string | null
  onGroup: (id: string) => void
  /** null until the panel has connected, and null for good while their
   *  protocol has no transmit command. */
  transmit: ((on: boolean) => void) | null
  /** The panel is open in another window, so this strip must not also hold a
   *  session — two in one browser share a device id. */
  panelElsewhere: boolean
}) {
  const [keyed, setKeyed] = useState(false)

  const canTalk = !!seat && !!transmit && !panelElsewhere && !!group

  function start() {
    if (!canTalk || keyed) return
    setKeyed(true)
    transmit?.(true)
  }
  function stop() {
    if (!keyed) return
    setKeyed(false)
    transmit?.(false)
  }

  // Space to talk, as on every dispatch console. Ignored while typing, or a
  // dispatcher writing a note would transmit every word.
  useEffect(() => {
    function down(e: KeyboardEvent) {
      if (e.code !== 'Space' || e.repeat) return
      const el = document.activeElement
      if (el && /INPUT|TEXTAREA|SELECT/.test(el.tagName)) return
      e.preventDefault()
      start()
    }
    function up(e: KeyboardEvent) { if (e.code === 'Space') stop() }
    window.addEventListener('keydown', down)
    window.addEventListener('keyup', up)
    return () => {
      window.removeEventListener('keydown', down)
      window.removeEventListener('keyup', up)
    }
  })

  // Never leave the microphone open because the mouse came up somewhere else.
  useEffect(() => {
    window.addEventListener('mouseup', stop)
    window.addEventListener('blur', stop)
    return () => {
      window.removeEventListener('mouseup', stop)
      window.removeEventListener('blur', stop)
    }
  })

  const live = keyed || !!talking
  const mine = keyed || !!talking?.mine
  const usable = seats.filter((s) => s.usable)

  function pttReason(): string {
    if (panelElsewhere) return 'The panel is open in another window'
    if (!seat) return 'No dispatch connection'
    if (!transmit) return 'The panel has no transmit command yet — asked for, not shipped'
    if (!group) return 'The panel has not reported its talk groups yet'
    return 'Hold to talk, or hold SPACE'
  }

  return (
    <div className="radiobar">
      {/* Left: what the air is doing. */}
      <div className={`rb-state ${mine && live ? 'me' : live ? 'live' : 'idle'}`}>
        <span className="lt" />
        <div className="rwho">
          {keyed ? (
            <>
              Transmitting
              <small>{selected ? `filing to ${selected.number}` : 'no call selected'}</small>
            </>
          ) : talking ? (
            <>
              {talking.who}<small>on the air</small>
            </>
          ) : seat ? (
            <>
              Air clear<small>{group ?? 'no group'}</small>
            </>
          ) : (
            <>
              No connection<small>{usable.length ? 'none free' : 'none set up'}</small>
            </>
          )}
        </div>
      </div>

      {/* Middle: nothing at all unless somebody is on the air. A strip full of
          chrome while the channel is silent is noise the dispatcher learns to
          ignore, and then misses when it matters. */}
      <div className="rb-air">
        {note ? (
          <span className="rb-note">{note}</span>
        ) : live ? (
          <>
            <div className={`wave ${mine ? 'me' : 'on'}`}>
              <i /><i /><i /><i /><i /><i /><i />
            </div>
            <span className="rb-talker">
              {keyed ? 'You' : talking?.who}
            </span>
          </>
        ) : null}
      </div>

      {/* Right: which seat, which group, and the way onto the air. */}
      <span className={`rb-seat ${seat ? '' : 'off'}`}>
        {seat ? seat.label : 'off air'}
      </span>

      {groups.length > 0 ? (
        <select
          className="orgpick"
          value={group ?? ''}
          onChange={(e) => onGroup(e.target.value)}
          aria-label="Talk group"
          disabled={!seat || panelElsewhere}
        >
          {groups.map((g) => (
            <option key={g.id} value={g.id}>{g.name}</option>
          ))}
        </select>
      ) : (
        // No invented list. The panel has not said what its groups are, and
        // showing "Main group" when the panel might be on something else would
        // tell a dispatcher he is talking to people he is not.
        <span className="rb-seat off" title="The panel does not report its groups yet">
          groups pending
        </span>
      )}

      <button
        className={`ptt ${keyed ? 'on' : ''}`}
        disabled={!canTalk}
        title={pttReason()}
        onMouseDown={start}
        onTouchStart={(e) => { e.preventDefault(); start() }}
        onTouchEnd={(e) => { e.preventDefault(); stop() }}
      >
        <span>{keyed ? 'Transmitting' : 'Hold to talk'}</span>
        <small>SPACE</small>
      </button>
    </div>
  )
}
